import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { Roadmap } from '../components/Roadmap';
import { plannedPages } from '../data/plannedPages';
type PlannedPageProps = {
  slug: string;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function PlannedPage({
  slug
}: PlannedPageProps) {
  const page = plannedPages.find(p => p.slug === slug) ?? plannedPages[0];
  const index = plannedPages.findIndex(p => p.slug === page.slug);
  const next = plannedPages[(index + 1) % plannedPages.length];
  return <div className="bg-ink" data-id="element-227" data-mp="2v0ffvrjy1kot">
      <section className="border-b border-paper/10 pb-16 pt-32 md:pb-24 md:pt-44" data-id="element-228" data-mp="300uqwcgr6g3m">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-229" data-mp="3nqxheo50re04">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease
        }} className="text-sm text-paper/60" data-id="element-230" data-mp="3npe1iyn9z4yv">
            Page en préparation · Phase {page.phase}
          </motion.p>
          <h1 className="mt-4 overflow-hidden font-display text-6xl uppercase leading-[0.9] md:text-[8rem]" data-id="element-231" data-mp="oroeidhuxrhy">
            <motion.span className="block" initial={{
            y: '100%'
          }} animate={{
            y: '0%'
          }} transition={{
            duration: 0.3,
            delay: 0.05,
            ease
          }} data-id="element-232" data-mp="13gsyo087q5sp">
              {page.title}
            </motion.span>
          </h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.15,
          ease
        }} className="mt-8 max-w-3xl text-xl leading-relaxed text-paper/80 md:text-2xl" data-id="element-233" data-mp="17ftfxeb1l0dl">
            {page.goal}
          </motion.p>
        </div>
      </section>

      <section className="py-20 md:py-28" data-id="element-234" data-mp="3o7l5yplw023f">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-[1.6fr_1fr]" data-id="element-235" data-mp="3j75rtvts6qke">
          <div data-id="element-236" data-mp="cpnhw80e3r25">
            <h2 className="font-display text-4xl uppercase md:text-5xl" data-id="element-237" data-mp="cq6x3gyno1ch">Ce qui sera développé</h2>
            <ul className="mt-8 border-t border-paper/15" data-id="element-238" data-mp="grs4wnwc1pft">
              {page.contents.map((c, i) => <Reveal key={c.title} delay={i * 0.05} data-id="element-239" data-mp="lp258yl9flif" data-mpi={i}>
                  <li className="grid gap-2 border-b border-paper/15 py-6 md:grid-cols-[1fr_1.4fr] md:gap-8" data-id="element-240" data-mp="qon3t7wiprum">
                    <span className="text-lg font-semibold" data-id="element-241" data-mp="voj9dbkv5rca">{c.title}</span>
                    <span className="text-paper/70" data-id="element-242" data-mp="zpl8ztip05es">{c.text}</span>
                  </li>
                </Reveal>)}
            </ul>
          </div>

          <aside className="space-y-10 lg:pt-20" data-id="element-243" data-mp="14guy8t0bx6q5">
            <Reveal data-id="element-244" data-mp="19f4ruz6enz9n">
              <h2 className="text-sm font-semibold text-paper/60" data-id="element-245" data-mp="19fo2rvxn1ne6">Pour qui</h2>
              <p className="mt-2 text-lg" data-id="element-246" data-mp="1eer3532ob5ft">{page.audience}</p>
            </Reveal>
            <Reveal delay={0.05} data-id="element-247" data-mp="1jdlmq78gsy0z">
              <h2 className="text-sm font-semibold text-paper/60" data-id="element-248" data-mp="1jcsqw1b1nsxm">Fonctionnalités prévues</h2>
              <ul className="mt-3 space-y-2" data-id="element-249" data-mp="33vwhyy78iej1">
                {page.features.map(f => <li key={f} className="flex gap-3" data-id="element-250" data-mp="2yw0bmsb2eb0b">
                    <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-signal" data-id="element-251" data-mp="2yymjyu5s4ujl" />
                    <span data-id="element-252" data-mp="2tzje57hz6kak">{f}</span>
                  </li>)}
              </ul>
            </Reveal>
            <Reveal delay={0.1} data-id="element-253" data-mp="3mm4g11w2uesd">
              <h2 className="text-sm font-semibold text-paper/60" data-id="element-254" data-mp="3mmdx1zz59blx">Indicateur de succès</h2>
              <p className="mt-2 font-display text-2xl uppercase text-signal" data-id="element-255" data-mp="3hmht20qg5k6d">{page.kpi}</p>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-id="element-256" data-mp="3cu83wmx5oo55">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-257" data-mp="37xzulq5n3p5w">
          <h2 className="font-display text-4xl uppercase md:text-5xl" data-id="element-258" data-mp="37vdo7tuhjfp9">Feuille de route du site</h2>
          <p className="mt-3 max-w-xl text-paper/60" data-id="element-259" data-mp="992f0ft4kn6w">
            Les pages qui génèrent des demandes de booking passent en premier, le contenu pour les fans suit.
          </p>
          <div className="mt-10" data-id="element-260" data-mp="3l2az96vnz43f">
            <Roadmap currentPhase={page.phase} currentLabel={page.label} data-id="element-261" data-mp="3q2yygfu1qr8q" />
          </div>

          <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:justify-between" data-id="element-262" data-mp="3q0m8bfe4xakl">
            <Link to="/" className="inline-flex items-center gap-2 font-semibold text-paper/70 transition-colors duration-150 hover:text-paper" data-id="element-263" data-mp="3b7srnqn4moec">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-264" data-mp="7prbmu7ia7d3" /> Retour à l’accueil
            </Link>
            <Link to={`/${next.slug}`} className="group inline-flex items-center gap-2 font-semibold" data-id="element-265" data-mp="cnj2rm0btpg8">
              Page suivante : {next.label}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" data-id="element-266" data-mp="2wy7o8jv3322" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
}